// Phase 7 rollout gate, client side. The FIRST company-scoped assignment in a
// company turns company scoping on for payroll; if that would leave people with
// no payroll access, the server refuses with 409 entity_scope_not_ready and
// names them. Here we show that list in the branded confirm (never
// window.confirm) and, only on "Continue anyway", resend with
// acknowledgeLockout: true. Every other error is rethrown untouched so the
// caller toasts the backend's own message.
import React, { useCallback } from 'react';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { entityScopeNotReady, type LockoutAffected } from '@/services/rbacService';

const SHOWN = 8;

export function personLabel(p: Pick<LockoutAffected, 'name' | 'email' | 'userId'>): string {
  return p.name?.trim() || p.email?.trim() || `Someone (${p.userId.slice(0, 8)})`;
}

export const LockoutMessage: React.FC<{ affected: LockoutAffected[]; partial: number }> = ({
  affected,
  partial,
}) => {
  const shown = affected.slice(0, SHOWN);
  const more = affected.length - shown.length;
  return (
    <div className="space-y-3 text-left">
      <p>
        This is the first company-scoped role in your organization. Saving it turns on company
        scoping for payroll, and these people have no company assigned:
      </p>
      <ul className="max-h-48 overflow-y-auto rounded-[10px] border border-border divide-y divide-border">
        {shown.map((p) => (
          <li key={p.userId} className="px-3 py-2">
            <span className="block text-sm font-medium text-foreground">{personLabel(p)}</span>
            {p.permissions.length > 0 && (
              <span className="block text-xs text-muted-foreground">
                {p.permissions.length} payroll/compensation permission{p.permissions.length === 1 ? '' : 's'}
              </span>
            )}
          </li>
        ))}
        {more > 0 && (
          <li className="px-3 py-2 text-xs text-muted-foreground">+{more} more</li>
        )}
      </ul>
      <p className="font-medium text-foreground">
        They will stop seeing payroll and accounting until you give them companies.
      </p>
      {partial > 0 && (
        <p className="text-xs text-muted-foreground">
          {partial} more {partial === 1 ? 'person' : 'people'} will keep only part of their payroll
          access. Check “Company access readiness” for details.
        </p>
      )}
    </div>
  );
};

export type LockoutSaveResult<T> = { saved: true; value: T } | { saved: false };

/**
 * Wrap a scoped-assignment save. `send(ack)` performs the request; it is called
 * with ack=false first and with ack=true only after the admin confirms.
 */
export function useSaveWithLockoutConfirm() {
  const confirm = useConfirm();
  return useCallback(
    async <T,>(send: (acknowledgeLockout: boolean) => Promise<T>): Promise<LockoutSaveResult<T>> => {
      try {
        return { saved: true, value: await send(false) };
      } catch (e) {
        const refusal = entityScopeNotReady(e);
        if (!refusal) throw e;
        const ok = await confirm({
          title: 'Turn on company scoping for payroll?',
          message: <LockoutMessage affected={refusal.affected} partial={refusal.partial} />,
          confirmLabel: 'Continue anyway',
          cancelLabel: 'Cancel',
          danger: true,
        });
        if (!ok) return { saved: false };
        return { saved: true, value: await send(true) };
      }
    },
    [confirm],
  );
}
